import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/forms/signUp.css';

const API_URL = import.meta.env.API_URL || 'http://localhost:3000';

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    emailConfirm: '',
    password: '',
    passwordConfirm: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [apiError, setApiError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.email !== formData.emailConfirm) {
      newErrors.email = 'Email addresses do not match.';
    }
    
    if (formData.password !== formData.passwordConfirm) {
      newErrors.password = 'Passwords do not match.';
    }
    
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(prevState => !prevState);
    } else {
      setShowPasswordConfirm(prevState => !prevState);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      setApiError('');
      
      try {
        const tzOffsetInMins = new Date().getTimezoneOffset();
        const requestData = {
          email: formData.email,
          password: formData.password,
          tzOffsetInMins: tzOffsetInMins
        };

        const response = await fetch(`${API_URL}/user/auth/sign-up`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
          setShowModal(true);
        } else {
          setApiError(data.message || 'An error occurred during registration. Please try again.');
        }
      } catch (error) {
        setApiError('Failed to connect to the server. Please try again later.');
        console.error('SignUp error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleModalConfirm = () => {
    setShowModal(false);
    navigate('/apply');
  };

  return (
    <div className="signup-form">
      <h2>Sign Up</h2>
      {apiError && <div className="auth-error">{apiError}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            required
          />
        </div>
        <div className="form-group">
          <label>Confirm Email</label>
          <input
            type="email"
            name="emailConfirm"
            value={formData.emailConfirm}
            onChange={handleChange}
            placeholder="Confirm your email"
            required
          />
          {errors.email && <span className="error">{errors.email}</span>}
        </div>
        <div className="form-group">
          <label>Password (min. 9 characters)</label>
          <div className="password-input-container">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
            />
            <button 
              type="button" 
              className="password-toggle" 
              onClick={() => togglePasswordVisibility('password')}
              tabIndex="-1"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
        </div>
        <div className="form-group">
          <label>Confirm Password</label>
          <div className="password-input-container">
            <input
              type={showPasswordConfirm ? "text" : "password"}
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
            />
            <button 
              type="button" 
              className="password-toggle" 
              onClick={() => togglePasswordVisibility('passwordConfirm')}
              tabIndex="-1"
              aria-label={showPasswordConfirm ? "Hide password" : "Show password"}
            >
              {showPasswordConfirm ? "👁️" : "👁️‍🗨️"}
            </button>
          </div>
          {errors.password && <span className="error">{errors.password}</span>}
        </div>
        <button 
          type="submit" 
          className="submit-button"
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Sign Up'}
        </button>
      </form>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Registration Complete</h2>
            <p>Your account has been successfully created. Please log in to continue.</p>
            <div className="modal-buttons">
              <button className="modal-button save" onClick={handleModalConfirm}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp; 